// ============================================================================
// OINP scoring / eligibility engine — pure functions, no React, no I/O.
// Mirrored server-side (widget-server/src/assessments/oinpEngine.ts) so the
// authoritative result is always recomputed from the raw answers rather than
// trusted from the client.
// ============================================================================

import {
  OINP_RULES, FACTORS, MAX_SCORE, PENDING,
  TEER_POINTS, OCCUPATIONAL_CATEGORY_POINTS, WAGE_BANDS, ONTARIO_EXPERIENCE_POINTS,
  CANADIAN_EARNINGS_POINTS, LEGAL_STATUS_POINTS, EDUCATION_POINTS,
  CANADIAN_CREDENTIALS_POINTS, LOWEST_CLB_POINTS, OFFICIAL_LANGUAGES_POINTS,
  REGION_POINTS, REGIONS,
  EXPERIENCE_OPTIONS, LICENCE_EXEMPT_BLOCKED_NOCS, EDUCATION_RULE_EXCEPTIONS,
  LANGUAGE_MINIMUMS, isSkilledTradeNoc, RECENT_GRAD_LANGUAGE_EXEMPT,
  CPSO_CERTIFICATES,
  EMPLOYER_QUESTIONS, EMPLOYER_LOCATION_TIERS, CVOR_REQUIRED_NOCS,
  overallProvenance,
} from './rules';
import { resolveNoc } from './noc';

const POST_SECONDARY_LEVELS = new Set([
  'doctorate', 'masters', 'univ-cert-above-bachelor', 'bachelor',
  'ontario-college-grad-cert', 'univ-cert-below-bachelor',
  'college-diploma', 'apprenticeship',
]);

const isKnown = value => value !== PENDING && value !== undefined && typeof value === 'number';

/** Points for an hourly wage, from the banded table. */
export function wagePoints(wage) {
  const value = Number(wage);
  if (!Number.isFinite(value) || value < 0) return PENDING;
  const band = WAGE_BANDS.bands.find(b => value >= b.min);
  return band ? band.points : PENDING;
}

/** The wage band a given wage falls into (for boost hints). */
export function wageBandIndex(wage) {
  const value = Number(wage);
  return WAGE_BANDS.bands.findIndex(b => value >= b.min);
}

// ---------------------------------------------------------------------------
// Per-factor scoring. Each returns { points, known, detail }.
// `known: false` means the rule table has no verified value for THIS answer —
// the factor is excluded from the total and reported in `missingFactors`.
// ---------------------------------------------------------------------------
function scoreFactor(id, answers) {
  const noc = resolveNoc(answers.noc);

  switch (id) {
    case 'teer':
      return noc ? lookup(TEER_POINTS.table[noc.teer], `TEER ${noc.teer}`) : unknown('no NOC code');

    case 'occupationalCategory':
      return noc
        ? lookup(OCCUPATIONAL_CATEGORY_POINTS.table[noc.broadCategory], noc.broadCategoryLabel)
        : unknown('no NOC code');

    case 'wage': {
      // ontario.ca: "For self-employed physicians, the hourly wage scoring
      // factor is not applicable." Not a missing table — genuinely absent, so it
      // must not count against the physician's total or land in missingFactors.
      if (answers.path === 'physician') return notApplicable();
      const pts = wagePoints(answers.wage);
      const band = WAGE_BANDS.bands[wageBandIndex(answers.wage)];
      return lookup(pts, band ? band.label : null);
    }

    case 'ontarioExperience': {
      // Physicians: cumulative medical practice in Ontario replaces the
      // job-offer-position tier entirely.
      if (answers.path === 'physician') {
        return lookup(
          ONTARIO_EXPERIENCE_POINTS.physicianPractice[answers.practiceMonths],
          `${answers.practiceMonths} of medical practice in Ontario`
        );
      }

      // Two mutually exclusive tiers, never summed. Months in the job-offer
      // position is tried first; the Ontario-wide tier only applies when that
      // scores nothing (under 6 months, or not currently in the position).
      const primary = ONTARIO_EXPERIENCE_POINTS.inPosition[answers.monthsInPosition];
      if (primary === undefined) return unknown('no months-in-position answer');
      if (primary > 0) return lookup(primary, `${answers.monthsInPosition} in the job offer position`);

      const fallback = ONTARIO_EXPERIENCE_POINTS.inOntario[answers.monthsInOntario];
      return lookup(fallback, `under 6 months in position; ${answers.monthsInOntario} worked in Ontario`);
    }

    case 'canadianEarnings':
      return lookup(CANADIAN_EARNINGS_POINTS.table[answers.earnings], answers.earnings);

    case 'legalStatus':
      return lookup(LEGAL_STATUS_POINTS.table[answers.legalStatus], answers.legalStatus);

    case 'education':
      return lookup(EDUCATION_POINTS.table[answers.education], answers.education);

    case 'canadianCredentials':
      return lookup(CANADIAN_CREDENTIALS_POINTS.table[answers.canadianCredentials], `${answers.canadianCredentials} credential(s)`);

    case 'lowestClb':
      return lookup(LOWEST_CLB_POINTS.table[answers.clb], `CLB ${answers.clb}`);

    case 'officialLanguages':
      return lookup(OFFICIAL_LANGUAGES_POINTS.table[answers.bothLanguages], answers.bothLanguages);

    case 'region':
      return lookup(REGION_POINTS.table[answers.region], answers.region);

    default:
      return unknown('unknown factor');
  }
}

const lookup = (value, detail) => (isKnown(value)
  ? { points: value, known: true, detail: detail || null }
  : { points: null, known: false, detail: detail || null });

const unknown = reason => ({ points: null, known: false, detail: reason });

/**
 * The factor does not apply to this applicant at all — distinct from "we have no
 * verified table for it". A not-applicable factor is excluded from the total,
 * from the achievable maximum, AND from missingFactors, so it never suppresses a
 * score the way a missing table does.
 */
const notApplicable = () => ({ points: null, known: false, notApplicable: true, detail: null });

/** '9+' -> 9, '4-or-lower' -> 4, 'untested' -> null. */
function clbToNumber(clb) {
  if (clb === '9+') return 9;
  if (clb === '4-or-lower') return 4;
  if (clb === 'untested' || clb === undefined || clb === '') return null;
  const n = Number(clb);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------
// Eligibility gates
// ---------------------------------------------------------------------------
function evaluateGates(answers) {
  const noc = resolveNoc(answers.noc);
  const teer = noc ? noc.teer : null;
  const isTeer45 = teer === 4 || teer === 5;
  // A listed skilled trades occupation gets a lower language bar (CLB 5) and
  // needs only a secondary diploma. Determined from the official NOC group list,
  // so this is now a definite yes/no rather than a "needs verification".
  const isSkilledTrade = !isTeer45 && Boolean(noc) && isSkilledTradeNoc(noc.code);
  const gates = [];

  if (answers.path === 'physician') {
    // Physicians bypass the job offer and the employer entirely.
    const cpso = CPSO_CERTIFICATES.find(c => c.id === answers.cpso);
    gates.push({ id: 'ohip-billing', status: answers.ohip === 'yes' ? 'pass' : 'fail', note: null });
    gates.push({
      id: 'cpso-certificate',
      status: cpso ? (cpso.qualifies ? 'pass' : 'fail') : 'warn',
      note: cpso && !cpso.qualifies ? 'postgradLicenceDoesNotQualify' : null,
    });
  } else {
    // 1. Work experience — the ordered "first true wins" list.
    const chosen = EXPERIENCE_OPTIONS.find(o => o.id === answers.experience);
    let expStatus = chosen ? (chosen.passes ? 'pass' : 'fail') : 'warn';
    let expNote = null;
    // Truck and bus/transit drivers do not get the licence route: they still owe
    // the 6-month rule.
    if (
      answers.experience === 'licensed-to-practise' &&
      noc && LICENCE_EXEMPT_BLOCKED_NOCS.includes(noc.code)
    ) {
      expStatus = 'fail';
      expNote = 'licenceRouteBlockedForDrivers';
    }
    gates.push({ id: 'work-experience', status: expStatus, note: expNote });
  }

  // 2. Language minimum — NOT a flat CLB 6. ontario.ca sets it per TEER:
  //    CLB 6 for TEER 0–3, CLB 5 for TEER 0–3 skilled trades, CLB 4 for TEER 4–5.
  //    Recent eligible Ontario graduates are exempt from the test requirement,
  //    though scores still earn points.
  const gradExempt = RECENT_GRAD_LANGUAGE_EXEMPT && answers.experience === 'recent-grad-three-months';
  const requiredClb = isTeer45
    ? LANGUAGE_MINIMUMS.teer4to5
    : isSkilledTrade
      ? LANGUAGE_MINIMUMS.teer0to3SkilledTrades
      : LANGUAGE_MINIMUMS.teer0to3;
  const clbNumeric = clbToNumber(answers.clb);
  let langStatus;
  let langNote = null;
  if (gradExempt) {
    langStatus = 'pass';
    langNote = 'recentGradLanguageExempt';
  } else if (clbNumeric === null) {
    langStatus = 'fail';
  } else if (clbNumeric >= requiredClb) {
    langStatus = 'pass';
    if (isSkilledTrade && clbNumeric < LANGUAGE_MINIMUMS.teer0to3) {
      langNote = 'skilledTradesLanguageException';
    }
  } else {
    langStatus = 'fail';
  }
  gates.push({ id: 'language-minimum', status: langStatus, note: langNote });

  // 3. Education minimum — also TEER-dependent. TEER 4–5 and TEER 0–3 skilled
  //    trades need only a secondary diploma; other TEER 0–3 need a 1+ year
  //    post-secondary credential.
  const pswException = noc && EDUCATION_RULE_EXCEPTIONS[noc.code];
  const hasPostSecondary = POST_SECONDARY_LEVELS.has(answers.education);
  const hasSecondary = hasPostSecondary || answers.education === 'high-school';
  let eduStatus;
  let eduNote = pswException ? pswException.noteKey : null;
  if (isTeer45 || isSkilledTrade) {
    // Both need only a secondary school diploma.
    eduStatus = hasSecondary ? 'pass' : 'fail';
    if (isSkilledTrade && !hasPostSecondary && hasSecondary) {
      eduNote = 'skilledTradesEducationException';
    }
  } else if (hasPostSecondary) {
    eduStatus = 'pass';
  } else if (pswException && answers.education === 'college-diploma') {
    eduStatus = 'pass';
  } else {
    eduStatus = 'fail';
  }
  gates.push({ id: 'post-secondary-credential', status: eduStatus, note: eduNote });

  // 4. ECA — required where the credential is foreign. A Canadian credential
  //    makes it moot.
  let ecaStatus;
  if (answers.canadianCredentials && answers.canadianCredentials !== '0') ecaStatus = 'pass';
  else if (answers.eca === 'yes') ecaStatus = 'pass';
  else if (answers.eca === 'not-yet') ecaStatus = 'warn';
  else ecaStatus = 'fail';
  gates.push({ id: 'eca', status: ecaStatus, note: null });

  // 5. Legal status — a SCORING factor, not a blocker.
  //    ontario.ca is explicit that earning 0 points here (applying from outside
  //    Canada, visitor or maintained status) is not an eligibility failure. This
  //    gate previously failed 'no-status' and warned on 'outside-canada', which
  //    told applicants they were ineligible when they were not. It now only
  //    flags being in Canada without status, which is worth a human look.
  let statusGate = 'pass';
  let statusNote = null;
  if (!answers.legalStatus) {
    statusGate = 'warn';
  } else if (answers.legalStatus === 'no-status') {
    statusGate = 'warn';
    statusNote = 'noStatusNeedsAdvice';
  } else if (LEGAL_STATUS_POINTS.table[answers.legalStatus] === 0) {
    statusNote = 'legalStatusScoresZeroButEligible';
  }
  gates.push({ id: 'legal-status', status: statusGate, note: statusNote });

  return gates;
}

function verdictFromGates(gates) {
  if (gates.some(g => g.status === 'fail')) return 'not-eligible';
  if (gates.some(g => g.status === 'warn')) return 'needs-verification';
  return 'appears-eligible';
}

// ---------------------------------------------------------------------------
// Boost simulator — only emits a suggestion when BOTH the current and the
// target value are known, so it can never invent a delta.
// ---------------------------------------------------------------------------
function buildBoosts(answers, breakdown) {
  const boosts = [];
  const by = id => breakdown.find(f => f.id === id);

  // Language: lift the lowest CLB to the best known band.
  const clb = by('lowestClb');
  const bestClb = LOWEST_CLB_POINTS.table['9+'];
  if (clb?.known && isKnown(bestClb) && bestClb > clb.points) {
    boosts.push({ id: 'clb', gain: bestClb - clb.points, key: 'boostClb', params: { from: answers.clb, to: '9+' } });
  }

  // Region: the same job offer in the highest-scoring region.
  const region = by('region');
  const bestRegion = REGIONS.reduce((best, r) => (r.points > best.points ? r : best), REGIONS[0]);
  if (region?.known && bestRegion.points > region.points) {
    boosts.push({
      id: 'region',
      gain: bestRegion.points - region.points,
      key: 'boostRegion',
      params: { from: answers.region, to: bestRegion.id },
    });
  }

  // Second official language.
  const bilingual = by('officialLanguages');
  const bothKnown = OFFICIAL_LANGUAGES_POINTS.table['both'];
  if (bilingual?.known && isKnown(bothKnown) && bothKnown > bilingual.points) {
    boosts.push({ id: 'bilingual', gain: bothKnown - bilingual.points, key: 'boostBilingual', params: {} });
  }

  // An additional eligible Canadian credential.
  const creds = by('canadianCredentials');
  const bestCreds = CANADIAN_CREDENTIALS_POINTS.table['2+'];
  if (creds?.known && isKnown(bestCreds) && bestCreds > creds.points) {
    boosts.push({ id: 'credentials', gain: bestCreds - creds.points, key: 'boostCredential', params: {} });
  }

  // Wage: one band up.
  const wage = by('wage');
  const idx = wageBandIndex(answers.wage);
  if (wage?.known && idx > 0) {
    const next = WAGE_BANDS.bands[idx - 1];
    if (next.points > wage.points) {
      boosts.push({
        id: 'wage',
        gain: next.points - wage.points,
        key: 'boostWage',
        params: { target: next.min },
      });
    }
  }

  return boosts.sort((a, b) => b.gain - a.gain).slice(0, 5);
}

// ---------------------------------------------------------------------------
// Public: candidate assessment
// ---------------------------------------------------------------------------
/**
 * @param {object} answers raw wizard answers
 * @returns {object} assessment result, safe to persist verbatim
 */
export function evaluateCandidate(answers = {}) {
  const base = {
    kind: 'oinp_candidate',
    path: answers.path || null,
    rulesVersion: OINP_RULES.version,
    legalBasis: OINP_RULES.legalBasis,
    provenance: overallProvenance(),
    maxScore: MAX_SCORE,
    evaluatedAt: new Date().toISOString(),
  };

  // --- Branch C: no job offer yet. Nothing to score, nothing to gate.
  if (answers.path === 'neither') {
    return {
      ...base,
      scoreState: 'not-applicable',
      verdict: 'needs-job-offer',
      total: null,
      breakdown: [],
      gates: [],
      missingFactors: [],
      boosts: [],
    };
  }

  // --- Branches A and B share the scoring path. Physicians are scored on the
  // same factors minus hourly wage (115 rather than 130); their Ontario
  // experience comes from cumulative medical practice instead of a job offer.
  const isPhysician = answers.path === 'physician';
  const maxScore = isPhysician ? OINP_RULES.physicianMaxScore : MAX_SCORE;

  const breakdown = FACTORS.map(factor => {
    const scored = scoreFactor(factor.id, answers);
    return {
      id: factor.id,
      labelKey: factor.labelKey,
      max: factor.max,
      points: scored.points,
      known: scored.known,
      notApplicable: Boolean(scored.notApplicable),
      detail: scored.detail,
    };
  });

  // A not-applicable factor is not a gap — only an unanswered or untabled factor
  // suppresses the total.
  const missingFactors = breakdown.filter(f => !f.known && !f.notApplicable).map(f => f.id);
  const knownSubtotal = breakdown.reduce((sum, f) => sum + (f.known ? f.points : 0), 0);
  const gates = evaluateGates(answers);

  return {
    ...base,
    maxScore,
    scoreState: missingFactors.length === 0 ? 'complete' : 'incomplete-ruleset',
    verdict: verdictFromGates(gates),
    // A total is published ONLY when every applicable factor has a verified
    // value. A partial sum would read as a real score.
    total: missingFactors.length === 0 ? knownSubtotal : null,
    knownSubtotal,
    scoredMax: breakdown.reduce((sum, f) => sum + (f.known ? f.max : 0), 0),
    breakdown,
    gates,
    missingFactors,
    boosts: buildBoosts(answers, breakdown),
    strongestFactor: strongestFactor(breakdown),
    noc: resolveNoc(answers.noc),
    practiceMonths: isPhysician ? (answers.practiceMonths || null) : undefined,
  };
}

function strongestFactor(breakdown) {
  const scored = breakdown.filter(f => f.known && f.max > 0);
  if (!scored.length) return null;
  const best = scored.reduce((a, b) => (b.points / b.max > a.points / a.max ? b : a));
  return best.points > 0 ? { id: best.id, labelKey: best.labelKey, points: best.points, max: best.max } : null;
}

// ---------------------------------------------------------------------------
// Public: employer assessment
// ---------------------------------------------------------------------------
/** The location tier drives the revenue and headcount thresholds. */
export function employerTier(locationId) {
  return EMPLOYER_LOCATION_TIERS.find(t => t.id === locationId) || null;
}

/**
 * Resolve the question list for a given location answer, substituting the
 * dynamic thresholds. Returns the questions in display order.
 */
export function employerQuestions(locationId) {
  const tier = employerTier(locationId);
  return EMPLOYER_QUESTIONS.map(q => ({
    ...q,
    params: q.dynamic && tier
      ? { revenueFloor: tier.revenueFloor, minEmployees: tier.minCanadianOrPrEmployees }
      : {},
  }));
}

export function evaluateEmployer(answers = {}) {
  const tier = employerTier(answers.location);
  const items = EMPLOYER_QUESTIONS.map(question => {
    const answerId = answers[question.id];
    const option = question.options.find(o => o.id === answerId);
    return {
      id: question.id,
      promptKey: question.promptKey,
      answerId: answerId || null,
      status: option ? option.verdict : 'unanswered',
      reasonKey: option?.reasonKey || null,
    };
  });

  // The CVOR question only bites for the two driver NOCs; if the caller told us
  // the NOC and it is not one of those, a missing answer is not a gap.
  const cvorRelevant = !answers.noc || CVOR_REQUIRED_NOCS.includes(String(answers.noc));

  const relevant = items.filter(i => i.id !== 'cvor' || cvorRelevant || i.status !== 'unanswered');
  const failures = relevant.filter(i => i.status === 'fail');
  const warnings = relevant.filter(i => i.status === 'warn');
  const unanswered = relevant.filter(i => i.status === 'unanswered');

  let verdict;
  if (failures.length) verdict = 'not-eligible';
  else if (warnings.length || unanswered.length) verdict = 'needs-verification';
  else verdict = 'appears-eligible';

  return {
    kind: 'oinp_employer',
    rulesVersion: OINP_RULES.version,
    legalBasis: OINP_RULES.legalBasis,
    provenance: 'secondary',
    evaluatedAt: new Date().toISOString(),
    verdict,
    tier: tier ? { id: tier.id, revenueFloor: tier.revenueFloor, minEmployees: tier.minCanadianOrPrEmployees } : null,
    items,
    failureIds: failures.map(f => f.id),
    warningIds: warnings.map(w => w.id),
  };
}

// ---------------------------------------------------------------------------
// AI hand-off — plain text the intake assistant can read so it never re-asks
// something the wizard already collected.
// ---------------------------------------------------------------------------
export function buildCandidateAiSummary(answers, result) {
  const lines = [`Assessment: OINP Ontario Workforce Priority (${result.rulesVersion})`];
  lines.push(`Path: ${answers.path || 'n/a'}`);

  if (answers.path === 'neither') {
    lines.push('The user has no Ontario job offer yet and is not a self-employed physician.');
    lines.push('This stream is employer-initiated, so they cannot register an EOI until an employer submits a job offer.');
    return lines.join('\n');
  }

  if (answers.path === 'physician') {
    lines.push(`OHIP billing number: ${answers.ohip || 'n/a'}`);
    lines.push(`CPSO certificate: ${answers.cpso || 'n/a'}`);
    lines.push(`Cumulative medical practice in Ontario: ${answers.practiceMonths || 'n/a'}`);
    lines.push(`Eligibility verdict: ${result.verdict}`);
    lines.push('No EOI points grid is loaded for the physician path, so no score was shown to the user.');
    return lines.join('\n');
  }

  const noc = result.noc;
  lines.push(`NOC: ${noc ? `${noc.code} (TEER ${noc.teer}, ${noc.broadCategoryLabel})` : 'not provided'}`);
  lines.push(`Job offer region: ${answers.region || 'n/a'}`);
  lines.push(`Hourly wage: ${answers.wage != null ? `$${answers.wage}/hr` : 'n/a'}`);
  lines.push(`Work-experience route: ${answers.experience || 'n/a'}`);
  lines.push(`Months in the offered position: ${answers.monthsInPosition || 'n/a'}; total months worked in Ontario: ${answers.monthsInOntario || 'n/a'}`);
  lines.push(`Highest education: ${answers.education || 'n/a'}`);
  lines.push(`Canadian post-secondary credentials (1+ yr): ${answers.canadianCredentials ?? 'n/a'}`);
  lines.push(`ECA less than 5 years old: ${answers.eca || 'n/a'}`);
  lines.push(`Lowest CLB across four skills: ${answers.clb || 'n/a'}`);
  lines.push(`Both official languages at CLB 6+: ${answers.bothLanguages === 'both' ? 'yes' : 'no'}`);
  lines.push(`Status in Canada: ${answers.legalStatus || 'n/a'}`);
  lines.push(`Highest single-year Canadian earnings: ${answers.earnings || 'n/a'}`);
  lines.push(`Eligibility verdict: ${result.verdict}`);

  if (result.total != null) {
    lines.push(`Estimated EOI score: ${result.total} of ${result.maxScore}`);
  } else {
    lines.push(`EOI score NOT shown to the user — the points grid is incomplete for: ${result.missingFactors.join(', ')}.`);
    lines.push('Do not quote a numeric EOI score to this user.');
  }

  const failed = result.gates.filter(g => g.status !== 'pass');
  if (failed.length) {
    lines.push(`Eligibility items needing attention: ${failed.map(g => `${g.id} (${g.status})`).join(', ')}`);
  }
  return lines.join('\n');
}

export function buildEmployerAiSummary(result) {
  const lines = [`Assessment: OINP employer eligibility (${result.rulesVersion})`];
  if (result.tier) {
    lines.push(`Work location tier: ${result.tier.id} — revenue floor $${result.tier.revenueFloor.toLocaleString()}, min ${result.tier.minEmployees} full-time Canadian/PR employees at the location`);
  }
  lines.push(`Verdict: ${result.verdict}`);
  if (result.failureIds.length) lines.push(`Hard failures: ${result.failureIds.join(', ')}`);
  if (result.warningIds.length) lines.push(`Needs verification: ${result.warningIds.join(', ')}`);
  return lines.join('\n');
}
